import { useApi } from '@api/ApiProvider'
import { MAGIC_PLACE_STATUSES, type MagicPlaceProposal, type MagicPlaceStatus } from '@api/types'
import { MagicStatusBadge } from '@features/magic/MagicStatusBadge'
import { ModerationActions } from '@features/magic/ModerationActions'
import { STAFF_MAGIC_KEY } from '@features/magic/useModeration'
import { mapHrefForMagicPlace } from '@features/map/focus'
import { useI18n } from '@i18n/I18nProvider'
import { Icon } from '@ui/Icon'
import { Photo } from '@ui/Photo'
import { ErrorState, LoadingState } from '@ui/States'
import { Link, useSearchParams } from 'wouter'
import { parsePage } from './filters'
import { Pager } from './Pager'
import { formatDateTime } from './period'
import { usePanelQuery } from './StaffSession'
import { PAGE_SIZE } from './TreesPage'

const CHIP = 'inline-flex min-h-12 items-center rounded-full border-2 px-4 font-semibold'

// Filtros en la URL (`?estado=received&pagina=2`), como el resto del panel.
export const parseMagicStatus = (value: string | null): MagicPlaceStatus | undefined =>
	MAGIC_PLACE_STATUSES.find((status) => status === value)

const useMagicFilters = () => {
	const [params, setParams] = useSearchParams()
	const status = parseMagicStatus(params.get('estado'))
	const page = parsePage(params.get('pagina'))
	const update = (next: { status?: MagicPlaceStatus; page: number }) => {
		const search = new URLSearchParams()
		if (next.status) search.set('estado', next.status)
		if (next.page > 1) search.set('pagina', String(next.page))
		setParams(search)
	}
	return {
		status,
		page,
		setStatus: (next: MagicPlaceStatus | undefined) => update({ status: next, page: 1 }),
		setPage: (next: number) => update({ status, page: next }),
	}
}

// Un lugar mágico en el panel: foto (se abre en grande), nombre, estado, cuándo, en qué idioma,
// quién (identificador anónimo, solo para el personal), "Ver en el mapa" y las acciones.
export const MagicProposalCard = ({ proposal }: { proposal: MagicPlaceProposal }) => {
	const { t, locale } = useI18n()
	return (
		<article className="rounded-3xl bg-white p-4 shadow-sm">
			<div className="flex gap-4">
				<a href={proposal.photoUrl} target="_blank" rel="noopener noreferrer" className="shrink-0">
					<Photo
						src={proposal.photoUrl}
						alt={t('magic.photoAlt', { name: proposal.name })}
						className="size-24 rounded-2xl object-cover"
					/>
				</a>
				<div className="min-w-0 flex-1">
					<MagicStatusBadge status={proposal.status} />
					<h2 className="mt-1 text-xl font-bold break-words">{proposal.name}</h2>
					<p className="text-sm text-earth-800">
						{formatDateTime(proposal.proposedAt, locale)} · {t('magic.language', { lang: proposal.locale })}
					</p>
					<p className="text-sm break-all text-earth-800">
						{proposal.anonymousId
							? t('magic.anonymous', { id: proposal.anonymousId })
							: t('magic.noAnonymous')}
					</p>
					{/* `~`: ruta de la app, fuera del panel (que va anidado en /panel). */}
					<Link
						href={`~${mapHrefForMagicPlace(proposal.id)}`}
						className="inline-flex min-h-12 items-center gap-1 text-sm font-semibold text-forest-800"
					>
						<Icon name="map" className="size-4" />
						{t('magic.viewOnMap')}
					</Link>
				</div>
			</div>
			<div className="mt-3">
				<ModerationActions id={proposal.id} status={proposal.status} />
			</div>
		</article>
	)
}

// Pestaña "Lugares": los lugares mágicos que envían los caminantes, filtrables por estado.
export const MagicPlacesPage = () => {
	const { t } = useI18n()
	const api = useApi()
	const { status, page, setStatus, setPage } = useMagicFilters()
	const list = usePanelQuery([...STAFF_MAGIC_KEY.slice(1), 'list', status ?? 'all', page], (token) =>
		api.magicPlaceProposals(token, { status, page, limit: PAGE_SIZE })
	)
	const options: [MagicPlaceStatus | undefined, string][] = [
		[undefined, t('magic.all')],
		...MAGIC_PLACE_STATUSES.map((s): [MagicPlaceStatus, string] => [s, t(`magic.status.${s}`)]),
	]

	return (
		<>
			<fieldset className="mb-4">
				<legend className="sr-only">{t('magic.filter')}</legend>
				<div className="flex flex-wrap gap-2">
					{options.map(([value, label]) => {
						const selected = value === status
						return (
							<button
								key={value ?? 'all'}
								type="button"
								aria-pressed={selected}
								onClick={() => setStatus(value)}
								className={`${CHIP} ${selected ? 'border-forest-800 bg-forest-800 text-white' : 'border-earth-300 bg-white text-forest-900'}`}
							>
								{label}
							</button>
						)
					})}
				</div>
			</fieldset>
			{list.isPending ? (
				<LoadingState shape="list" />
			) : !list.data ? (
				<ErrorState error={list.error} onRetry={() => list.refetch()} />
			) : list.data.items.length === 0 ? (
				<p className="py-8 text-center text-lg text-earth-800">{t('magic.empty')}</p>
			) : (
				<>
					<p className="mb-3 text-earth-800">{t('magic.count', { count: list.data.meta.total })}</p>
					<ul className="grid gap-3">
						{list.data.items.map((proposal) => (
							<li key={proposal.id}>
								<MagicProposalCard proposal={proposal} />
							</li>
						))}
					</ul>
					<Pager meta={list.data.meta} onChange={setPage} />
				</>
			)}
		</>
	)
}
